import { useState, useEffect } from "react";
import { THEME, TYPOGRAPHY, SPACING } from "@shared/constants/designSystem";
import { useWebsite } from "@shared/hooks";
import { useSiteGradients } from "@shared/hooks";
import { DEFAULT_BUSINESS_TYPE } from "@shared/constants/businessTypes";
import Button from "@shared/components/base/Button";

const CONSENT_KEY = "cookie-consent";
const CONSENT_VERSION = "1.0"; // Increment this to re-show banner after policy changes

function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { name, businessType = DEFAULT_BUSINESS_TYPE } = useWebsite();
  const gradients = useSiteGradients();

  useEffect(() => {
    // Check if user has already consented
    try {
      const consent = localStorage.getItem(CONSENT_KEY);
      const consentData = consent ? JSON.parse(consent) : null;

      // Show banner if no consent or version changed
      if (!consentData || consentData.version !== CONSENT_VERSION) {
        setShowBanner(true);
      }
    } catch {
      // If localStorage fails, show banner to be safe
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    try {
      const consentData = {
        accepted: true,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
        types: ["essential", "functional"], // Track what they consented to
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
      closeBanner();

      // Reload to apply consent to any pending storage operations
      window.location.reload();
    } catch (error) {
      console.error("Failed to save consent:", error);
      closeBanner();
    }
  };

  const handleDecline = () => {
    try {
      const consentData = {
        accepted: false,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
        types: ["essential"], // Only essential cookies/storage
      };
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));

      // Clear non-essential localStorage items
      clearNonEssentialStorage();
      closeBanner();
    } catch (error) {
      console.error("Failed to save consent:", error);
      closeBanner();
    }
  };

  const closeBanner = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowBanner(false);
    }, 300); // Match animation duration
  };

  const clearNonEssentialStorage = () => {
    // Keep only essential items
    const essentialKeys = [CONSENT_KEY, "theme"]; // Theme is essential for accessibility
    const allKeys = Object.keys(localStorage);

    allKeys.forEach((key) => {
      if (!essentialKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });
  };

  if (!showBanner) return null;

  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-50 
        transform transition-transform duration-300 ease-out
        ${isClosing ? "translate-y-full" : "translate-y-0"}
      `}
      role="dialog"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-description"
    >
      <div
        className={`
        ${THEME.COLORS.BG.DEFAULT} 
        border-t ${THEME.COLORS.BORDER.DEFAULT}
        shadow-lg
      `}
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${SPACING.DEFAULT}`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <h3
                className={`${TYPOGRAPHY.HEADING.H4} ${THEME.COLORS.TEXT.DEFAULT} mb-2`}
              >
                Cookie & Privacy Settings
              </h3>
              <p
                id="cookie-consent-description"
                className={`${TYPOGRAPHY.BODY.SMALL} ${THEME.COLORS.TEXT.MUTED}`}
              >
                We use local storage (similar to cookies) to enhance your
                experience on the {name} website.
                {businessType === "musician" && (
                  <>
                    This includes remembering your shopping cart contents and
                    gallery view settings.{" "}
                  </>
                )}
                {businessType === "software" && (
                  <>
                    This includes remembering your app preferences and usage
                    settings.{" "}
                  </>
                )}
                {businessType === "service" && (
                  <>
                    This includes remembering your preferences and form
                    data.{" "}
                  </>
                )}
                Your theme preference is always saved for accessibility. By
                clicking &quot;Accept&quot;, you consent to our use of these
                features. See our{" "}
                <a
                  href="/privacy"
                  className="text-primary-600 dark:text-primary-400 underline hover:no-underline"
                >
                  Privacy Policy
                </a>{" "}
                for more details.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDecline}
                className={`
                  ${THEME.COLORS.BORDER.DEFAULT} 
                  ${THEME.COLORS.TEXT.DEFAULT}
                  hover:${THEME.COLORS.BG.MUTED}
                `}
              >
                Essential Only
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAccept}
                className={`
                  ${gradients.componentsButton}
                  ${THEME.COLORS.TEXT.DEFAULT}
                  hover:shadow-lg hover:scale-[1.02]
                  transition-all duration-300
                `}
              >
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
